import { computed, effect } from '@preact/signals-react'
import type { RoomStateInternal as RoomState } from '../../room/roomState/roomState'
import type { Bot } from '../bot/bot.types'
import type { ActiveGame } from '../../room/room.types'
import type { AiTurnRunner } from './aiTurnRunner.types'

import type { RoomProxyManager } from '../../room/roomProxy/roomProxyManager'

export interface AiTurnRunnerDeps {
  bot: Bot
  roomState: RoomState
  roomProxyManager: RoomProxyManager
  onError: (msg: string) => void
}

function getTurnKey(activeGame: ActiveGame | null): string {
  if (!activeGame) return 'none'
  const ctx: any = activeGame.ctx || {}
  return [
    activeGame.gameId || '',
    String(ctx.currentPlayer ?? ''),
    String(ctx.turn ?? ''),
    String(ctx.numMoves ?? ''),
    String(ctx.phase ?? ''),
    String(ctx.round ?? ''),
    String(Boolean(ctx.gameover)),
    String(activeGame.playerToMateIds?.length ?? ''),
  ].join('|')
}

export function makeAiTurnRunner(deps: AiTurnRunnerDeps): AiTurnRunner {
  let lastProcessedKey: string | null = null
  let retryTimer: ReturnType<typeof setTimeout> | null = null
  let retryKey: string | null = null
  let retryAttempt = 0

  const progressMessages = computed(() => deps.bot.progressMessages.value)
  const percentComplete = computed(() => deps.bot.percentComplete.value)
  const isProcessing = computed(() => deps.bot.isProcessing.value)

  effect(() => {
    void onActiveGameUpdated(deps.roomState.activeGame.value)
  })

  function clearRetry(): void {
    if (retryTimer) clearTimeout(retryTimer)
    retryTimer = null
    retryKey = null
    retryAttempt = 0
  }

  function retryMove(key: string): void {
    retryKey = key
    const delay = Math.min(500 * 2 ** retryAttempt, 30_000)
    retryAttempt += 1
    retryTimer = setTimeout(() => {
      retryTimer = null
      void onActiveGameUpdated(deps.roomState.activeGame.value)
    }, delay)
  }

  async function onActiveGameUpdated(activeGame: ActiveGame | null): Promise<void> {
    const key = getTurnKey(activeGame)
    if (retryKey && retryKey !== key) clearRetry()

    // Cancel on game end / close.
    if (!activeGame || activeGame.ctx?.gameover) {
      clearRetry()
      deps.bot.cancel()
      lastProcessedKey = null
      return
    }

    // If we already processed this exact turn snapshot, do nothing.
    if (lastProcessedKey === key || (retryTimer && retryKey === key)) return

    const currentBgioPlayerID = activeGame.ctx?.currentPlayer
    const playerToMateIds = activeGame.playerToMateIds
    if (!currentBgioPlayerID || !playerToMateIds) return

    const bgioIndex = parseInt(currentBgioPlayerID)
    if (!Number.isFinite(bgioIndex) || bgioIndex < 0 || bgioIndex >= playerToMateIds.length) return

    const matesForPlayer = playerToMateIds[bgioIndex]
    // A bot only acts when it is the sole mate assigned to the logical player.
    if (matesForPlayer?.length !== 1) return

    const currentMateID = matesForPlayer[0]
    if (!currentMateID) return

    const currentPlayer: any = (deps.roomState.mates.value || []).find((m: any) => m?.mateId === currentMateID)

    const isAI = Boolean(currentPlayer?.isAI) || deps.roomState.localAIPlayerIDs.value.has(currentMateID)
    if (!isAI) {
      // If we were computing for a previous AI turn but state changed to a human turn, cancel.
      clearRetry()
      if (deps.bot.isProcessing.value) deps.bot.cancel()
      lastProcessedKey = null
      return
    }

    // If already computing, don't start a second run. (We don't set lastProcessedKey here
    // because we didn't actually act yet.)
    if (deps.bot.isProcessing.value) return

    // Check if we can make moves
    // Check if we can make moves
    const proxy = deps.roomProxyManager.currentRoomProxy.value
    // We can assume if proxy exists, we can use it.
    if (!proxy) return

    const move = await deps.bot.chooseMove({
      gameId: activeGame.gameId,
      gameState: activeGame.gameState,
      ctx: activeGame.ctx,
      bgioPlayerId: currentBgioPlayerID,
      gameVersion: activeGame.gameVersion,
      botType: currentPlayer?.botType,
      botConfig: currentPlayer?.botConfig,
    })

    // If compute was cancelled or failed, don't mark the key as processed.
    if (!move) return

    // A cancellation or a room update may have happened while the bot was thinking.
    if (getTurnKey(deps.roomState.activeGame.value) !== key) return

    try {
      // Use proxy if available (unified path for local and cloud)
      if (proxy) {
        const result = await proxy.makeMove({ move, mateId: currentMateID })
        if (!result.success) {
          deps.onError(`AI move failed: ${result.error || 'Unknown error'}`)
          retryMove(key)
          return
        }
      }
      // Prevent duplicate submissions only after the room accepted the move.
      lastProcessedKey = key
      clearRetry()
    } catch (err: any) {
      deps.onError(`AI move failed: ${err?.message || 'Unknown error'}`)
      retryMove(key)
    }
  }

  function cancel(): void {
    clearRetry()
    deps.bot.cancel()
    lastProcessedKey = null
  }

  return {
    onActiveGameUpdated,
    cancel,
    progressMessages,
    percentComplete,
    isProcessing,
  }
}
